import { doc, getDoc, setDoc, updateDoc, deleteDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseDDH';

const COLLECTION = 'trackingLinks';

const COMBINING_DIACRITICS = new RegExp('[̀-ͯ]', 'g');

function slugify(name) {
  return name
    .toLowerCase()
    .normalize('NFKD').replace(COMBINING_DIACRITICS, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'link';
}

function offerFieldsFor(offer) {
  const hasDescription = offer?.type === 'custom' || offer?.type === 'free';
  return {
    offerType: offer?.type || null,
    discountPercent: offer?.type === 'discount' ? offer.discountPercent : null,
    customPrice: offer?.type === 'custom' ? offer.customPrice : null,
    customDescription: hasDescription ? (offer.customDescription || null) : null,
  };
}

// Independent of the base-price offer above: adjusts the Web/App care plan
// prices specifically, per tier. Each of `essential`/`active` is optional:
// { mode: 'percent'|'free'|'custom', percent, custom }
function carePlanFieldsFor(carePlan) {
  const essentialMode = carePlan?.essential?.mode || 'none';
  const activeMode = carePlan?.active?.mode || 'none';
  return {
    essentialPlanMode: essentialMode,
    essentialPlanPercent: essentialMode === 'percent' ? carePlan.essential.percent : null,
    essentialPlanCustom: essentialMode === 'custom' ? carePlan.essential.custom : null,
    activePlanMode: activeMode,
    activePlanPercent: activeMode === 'percent' ? carePlan.active.percent : null,
    activePlanCustom: activeMode === 'custom' ? carePlan.active.custom : null,
  };
}

// Deterministic, name-derived tokens: readable, and a repeat link for the
// same company reuses its existing doc/stats instead of forking a new one.
// Trade-off (chosen deliberately): the token is guessable, so anyone could
// probe for other companies' links, fake a visit on one, or read its
// (non-sensitive) offer details. Fine for this low-stakes internal use case.
//
// `offer` is optional: { type: 'discount'|'custom', discountPercent, customPrice, customDescription }
// `category` is 'website' or 'app'.
// `carePlan` is optional and independent of `offer`:
//   { essential: { mode, percent, custom }, active: { mode, percent, custom } }
export async function createTrackingLink(name, createdByEmail, offer = null, category = 'website', carePlan = null) {
  const trimmedName = name.trim();
  const token = `welcome-${slugify(trimmedName)}`;
  const ref = doc(db, COLLECTION, token);
  const existing = await getDoc(ref);
  const reused = existing.exists();
  const fields = { ...offerFieldsFor(offer), ...carePlanFieldsFor(carePlan) };

  if (reused) {
    // Update the personalization only — leave visit stats untouched.
    await setDoc(ref, { name: trimmedName, category, ...fields }, { merge: true });
  } else {
    await setDoc(ref, {
      name: trimmedName,
      createdBy: createdByEmail,
      createdAt: serverTimestamp(),
      visited: false,
      visitCount: 0,
      lastVisitedAt: null,
      category,
      ...fields,
    });
  }
  return { token, reused };
}

// Edits an existing link in place — the token (and therefore the URL
// already sent out) never changes, only its name, category and offer details.
export async function updateTrackingLink(token, name, offer = null, category = 'website', carePlan = null) {
  await updateDoc(doc(db, COLLECTION, token), {
    name: name.trim(),
    category,
    ...offerFieldsFor(offer),
    ...carePlanFieldsFor(carePlan),
  });
}

export async function getTrackingLink(token) {
  const snap = await getDoc(doc(db, COLLECTION, token));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function trackingLinkUrl(token) {
  return `${window.location.origin}/${token}`;
}

export async function recordVisit(token) {
  const ref = doc(db, COLLECTION, token);
  await updateDoc(ref, {
    visited: true,
    visitCount: increment(1),
    lastVisitedAt: serverTimestamp(),
  });
}

export async function deleteTrackingLink(token) {
  await deleteDoc(doc(db, COLLECTION, token));
}

export function formatVisitDate(ts) {
  return ts ? ts.toDate().toLocaleString() : null;
}

export function descriptionBullets(customDescription) {
  return (customDescription || '')
    .split('\n')
    .map((line) => line.replace(/^[-*•]\s*/, '').trim())
    .filter(Boolean);
}
